import { useState, useCallback, useMemo } from 'react';
import type { TestState, LevelResult, Screen } from '../types';
import { maxHRForProtocol, referenceHRForProtocol } from '../../supabase/functions/_shared/clinic-protocol';
import { CONSUMER_PROTOCOL_VERSION } from '../utils/consumerProtocol';
import { LEVELS } from '../utils/protocol';

function getTimeOfDay(hour: number): string {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

const initialState: TestState = {
  protocolVersion: CONSUMER_PROTOCOL_VERSION,
  firstName: '',
  lastName: '',
  age: 30,
  sex: 'male',
  betaBlocker: false,
  maxHR: 190,
  stopHR: 152,
  restingHR: null,
  currentLevel: 1,
  data: [],
  devMode: false,
  testedAtHour: null,
  testedAtTimeOfDay: null,
};

const demoReportState: TestState = {
  protocolVersion: CONSUMER_PROTOCOL_VERSION,
  firstName: 'Demo',
  lastName: 'User',
  age: 37,
  sex: 'female',
  betaBlocker: false,
  maxHR: 183,
  stopHR: 146.4,
  restingHR: 62,
  currentLevel: 5,
  data: [
    { level: 1, hr: 104, rpe: 2, vo2Estimate: LEVELS[0].vo2 },
    { level: 2, hr: 116, rpe: 3, vo2Estimate: LEVELS[1].vo2 },
    { level: 3, hr: 128, rpe: 4, vo2Estimate: LEVELS[2].vo2 },
    { level: 4, hr: 141, rpe: 6, vo2Estimate: LEVELS[3].vo2 },
    { level: 5, hr: 154, rpe: 7, vo2Estimate: LEVELS[4].vo2 },
  ],
  devMode: true,
  testedAtHour: 9,
  testedAtTimeOfDay: 'morning',
};

export function useTestState() {
  const [state, setState] = useState<TestState>(initialState);
  const [screen, setScreen] = useState<Screen>('landing');

  const updateSetup = useCallback(
    (fields: Partial<Pick<TestState, 'firstName' | 'lastName' | 'age' | 'sex' | 'betaBlocker'>>) => {
      setState((s) => {
        const next = { ...s, ...fields };
        const age = next.age;
        const bb = next.betaBlocker;
        const mhr = maxHRForProtocol(CONSUMER_PROTOCOL_VERSION, age, bb);
        return { ...next, protocolVersion: CONSUMER_PROTOCOL_VERSION, maxHR: mhr, stopHR: referenceHRForProtocol(CONSUMER_PROTOCOL_VERSION, mhr) };
      });
    },
    [],
  );

  const setRestingHR = useCallback((hr: number | null) => {
    setState((s) => ({ ...s, restingHR: hr }));
  }, []);

  const toggleDevMode = useCallback(() => {
    setState((s) => ({ ...s, devMode: !s.devMode }));
  }, []);

  const logLevel = useCallback((hr: number, rpe: number) => {
    setState((s) => {
      const proto = LEVELS[s.currentLevel - 1];
      const result: LevelResult = {
        level: s.currentLevel,
        hr,
        rpe,
        vo2Estimate: proto.vo2,
      };
      return {
        ...s,
        data: [...s.data, result],
      };
    });
  }, []);

  const advanceLevel = useCallback(() => {
    setState((s) => ({ ...s, currentLevel: s.currentLevel + 1 }));
  }, []);

  const checkStopConditions = useCallback((): { shouldStop: boolean; reason: string; hrAdvisory?: boolean } => {
    const lastEntry = state.data[state.data.length - 1];
    if (!lastEntry) return { shouldStop: false, reason: '' };

    // RPE >= 8: stop regardless of HR
    if (lastEntry.rpe >= 8) {
      return { shouldStop: true, reason: `RPE ${lastEntry.rpe} reached stop zone` };
    }

    // The continuation exception requires examiner judgement in the clinic workflow.
    if (lastEntry.hr >= state.stopHR && lastEntry.rpe < 8) {
      return { shouldStop: true, reason: '80% HR reference reached during a self-guided test. A clinician must assess any continuation.' };
    }

    // Level 5 done
    if (state.currentLevel >= 5) {
      return { shouldStop: true, reason: 'All 5 levels completed' };
    }

    return { shouldStop: false, reason: '' };
  }, [state.data, state.stopHR, state.currentLevel]);

  const captureTestTime = useCallback(() => {
    const hour = new Date().getHours();
    setState((s) => ({ ...s, testedAtHour: hour, testedAtTimeOfDay: getTimeOfDay(hour) }));
  }, []);

  const resetTest = useCallback(() => {
    setState(initialState);
    setScreen('landing');
  }, []);

  const loadDemoReport = useCallback(() => {
    setState(demoReportState);
    setScreen('results');
  }, []);

  const lastHR = useMemo(() => {
    if (state.data.length === 0) return null;
    return state.data[state.data.length - 1].hr;
  }, [state.data]);

  return {
    state,
    screen,
    setScreen,
    updateSetup,
    setRestingHR,
    toggleDevMode,
    logLevel,
    advanceLevel,
    checkStopConditions,
    captureTestTime,
    resetTest,
    loadDemoReport,
    lastHR,
  };
}
